import React, { useEffect, useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Camera, Loader2 } from 'lucide-react';
import { compressImage } from '../../lib/photos';
import { formatApiError } from '../../api';

// Picks + previews a profile photo. `onFile(file)` gets the compressed JPEG;
// when `upload` is provided the component uploads immediately and reports via onUploaded.
export const PhotoPicker = ({ currentSrc, initials = 'D', onFile, upload, onUploaded, size = 'w-24 h-24', testId = 'photo-picker' }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const handleChange = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    setError('');
    try {
      const file = await compressImage(picked);
      setPreview(URL.createObjectURL(file));
      onFile?.(file);
      if (upload) {
        setBusy(true);
        const result = await upload(file);
        onUploaded?.(result);
      }
    } catch (err) {
      setError(err?.response ? formatApiError(err) : err?.message || 'Could not use this photo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <Avatar className={`${size} ring-2 ring-border`}>
        <AvatarImage src={preview || currentSrc} className="object-cover" data-testid={`${testId}-preview`} />
        <AvatarFallback className="text-xl">{initials}</AvatarFallback>
      </Avatar>
      <div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" capture="user" className="hidden"
          onChange={handleChange} data-testid={`${testId}-input`} />
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testId}-btn`}>
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
          {preview || currentSrc ? 'Change photo' : 'Add photo'}
        </Button>
        <p className="text-xs text-muted-foreground mt-1">Clear face photo · JPG/PNG · shown to riders</p>
        {error && <p className="text-xs text-red-600 mt-1" data-testid={`${testId}-error`}>{error}</p>}
      </div>
    </div>
  );
};
